import moment from 'moment/moment';
import mapSort from 'mapsort';
import { translate } from '@/translation/translate.ts';
import { SelectFieldOption } from '@/eventConfig/types.ts';

const clean = (str: string): string => {
  return str
    .replace(/^\s+/, '') // Remove whitespaces at the beginning. For some reason not the end.
    .replace(/\s+/g, ' '); // Replace all multiple-occurences of whitespace with a single one.
};

const ucWords = (str: string): string => {
  const doNotTouch = new Set(['van', 'von', 'und', 'zu', 'der', 'die', 'med.', 'dent.', 'phil.', 'unter', 'an', 'am']);

  return str.replace(
    /\b[^\s-./-]+\b/g,
    (word, offset) =>
      offset === 0
        ? ucFirst(word) // Always capitalize first letter
        : doNotTouch.has(word.toLowerCase())
          ? word.toLowerCase() // words from doNotTouch remain in lower case
          : ucFirst(word) // other words also have the first letter capitalized
  );
};

const ucFirst = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const lowerAndTrim = (str: string): string => {
  return str.trim().toLowerCase();
};

const upper = (str: string): string => {
  return str.toUpperCase();
};

const formatPhoneNumber = (phoneNumber: string | number, countryCode: string): string => {
  const countryCodeRegex = new RegExp(`^((00|\\+)?${countryCode})?0?([0-9]+)$`);
  const formattedPhoneNumber = String(phoneNumber)
    .replace(/[^+\d]/g, '') // Keep only digits and '+'
    .replace(/^00(\d+)$/, '+$1') // Convert leading '00' to '+'
    .replace(countryCodeRegex, `+${countryCode}$3`);

  return formattedPhoneNumber;
};

/**
 * Summary
 *
 * The delimit function formats a string by splitting it into segments of
 * specified lengths and inserting a delimiter between them. The segments
 * are defined using an array of numbers or strings that indicate how many
 * characters should be in each segment.
 *
 * NOTE: This adds the delimiter to the end of a sequence, even if nothing
 * else follows. This might or might not be intentional, it is kept from the
 * previous implementation because nobody knows.
 * (i.e. Delimit "0011", digits: [2, 2, 4], delimiter "." --> "00.11."
 *
 * @param value The input string that needs to be formatted. This can include
 * any characters, and the function will handle splitting and inserting
 * delimiters according to the specified digits pattern.
 * @param digits An array that defines the lengths of the segments that
 *                the value should be split into. The array can contain:
 *
 *     Numbers: Indicating the number of characters for each segment.
 *     Strings: The function will calculate the length of the string and use
 *              that as the segment length. For example, 'MM' would be treated
 *              as 2 and 'YYYY' as 4
 *
 * @param delimiter The delimiter that should be inserted between the segments.
 *        This can be any string,
 */
const delimit = (value: string, digits: (string | number)[], delimiter: string): string => {
  const numericDigits = digits.map((digit) => (typeof digit === 'string' ? digit.length : digit));
  const joinedInput = value.split(delimiter).join('');

  return numericDigits.reduce<{ formatted: string; remaining: string }>(
    (acc, digit, i) => {
      if (acc.remaining.length === 0) {
        return acc; // Stop if no input remains
      }

      const newSegment = acc.remaining.slice(0, digit);
      const newRemaining = acc.remaining.slice(digit);

      return {
        formatted:
          acc.formatted +
          newSegment +
          (newSegment.length === digit && numericDigits[i + 1] !== undefined ? delimiter : ''),
        remaining: newRemaining,
      };
    },
    { formatted: '', remaining: joinedInput }
  ).formatted;
};

/**
 * The formatFeetInches function formats a string representing
 * feet and inches, ensuring the input is cleaned of non-numeric
 * characters and then correctly outputs the format as x'y",
 * where x represents feet and y represents inches.
 *
 * @param value string, anything that contains a few numbers will be converted to
 *              <num1>'<num2><num3>" or 1'23" or 1'2"
 */
const formatFeetInches = (value: string): string => {
  // Clean the input by removing any non-numeric characters
  const cleanedInput = value.replace(/[^\d]/g, '');

  const byChar = cleanedInput.split('');
  return byChar.reduce((formattedValue, digit, index) => {
    if (index === 0) {
      // Add feet with a single quote
      return formattedValue + digit + "'";
    } else if (index === 1) {
      // Add first inch digit and if it is the last digit also add the " foot denominator
      return formattedValue + digit + (byChar.length === 2 ? '"' : '');
    } else if (index === 2) {
      // Add second inch digit and closing quote
      return formattedValue + digit + '"';
    }
    return formattedValue;
  }, '');
};

const formatDate = (str: string, format: any, delimiter: any, re: any) => {
  // I'm not touching this code with a 10 km pole. Here is the original Commit:
  // Via: https://bitbucket.dkmsnet.lan/projects/WEB/repos/ereg/commits/9442a1810b5f7d50b325654b501498d3dfe23636#app%2Fereg%2Fsrc%2FtextFormatter.js
  const addDay = (day: any, isEnd: any) => {
    const fullDay = parseInt(day) > 3 || day.length === 2 ? day.padStart(2, '0') : isEnd ? day.padStart(2, '0') : day;
    const daysInMonth = moment(str, 'DD.MM.YYYY').daysInMonth();
    const maxDaysInMonth = isNaN(daysInMonth) ? '31' : daysInMonth;
    return fullDay > maxDaysInMonth ? maxDaysInMonth : fullDay;
  };
  const addMonth = (month: any, isEnd: any) =>
    parseInt(month) > 1 || month.length === 2 ? month.padStart(2, '0') : isEnd ? month.padStart(2, '0') : month;
  const addYear = (year: any) => {
    const thisYearInt = parseInt(moment().format('YY'));
    const yearInt = parseInt(year);

    let yearFormatted = year;
    if (year.length === 2 && (yearInt < 19 || yearInt > 20)) {
      yearFormatted = (year > thisYearInt ? '19' : '20') + yearFormatted;
    }

    return yearFormatted;
  };
  const autoFormatDate = (dateParts: any, formatParts: any[], inputText = '') => {
    let formatted = '';
    let part;
    const isEnd = delimiter === inputText.slice(-1);

    for (const fp of formatParts) {
      switch (fp) {
        case 'DD':
          if (dateParts.day) {
            part = addDay(dateParts.day, isEnd);
            formatted += part;
            if (part.length !== fp.length) {
              break;
            }
          } else {
            break;
          }
          break;

        case 'MM':
          if (dateParts.month) {
            part = addMonth(dateParts.month, isEnd);
            formatted += part;
            if (part.length !== fp.length) {
              break;
            }
          } else {
            break;
          }
          break;

        case 'YYYY':
          if (dateParts.year) {
            part = addYear(dateParts.year);
            formatted += part;
            if (part.length !== fp.length) {
              break;
            }
          } else {
            break;
          }
          break;

        default:
          continue;
      }
    }
    return formatted;
  };

  try {
    const m = str.match(new RegExp(re, ''));
    const formatted = autoFormatDate(m?.groups, format, str);

    return delimit(formatted, format, delimiter);
  } catch (_e) {
    return str;
  }
};

const sort = (options: SelectFieldOption[]): SelectFieldOption[] => {
  const sorted = mapSort(
    options,
    (e) => (e._order ? String(e._order) : '') + translate(e.label),
    (a, b) => a.localeCompare(b)
  );
  return sorted;
};

const numeric = (value: string) => {
  const digits = value.match(/\d+/g);
  return digits ? `${Number(digits.join(''))}` : '';
};

export const TextUtils = {
  clean,
  ucFirst,
  ucWords,
  lowerAndTrim,
  upper,
  formatPhoneNumber,
  delimit,
  formatFeetInches,
  formatDate,
  sort,
  numeric,
};
